import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import useLoginStore from "../stores/loginStore";

// API 응답 타입 정의
export interface IApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
  error?: {
    code: string;
    detail: string;
  };
}

// Get API endpoint from environment variables or default
const API_ENDPOINT =
  import.meta.env.VITE_API_ENDPOINT || "https://api.snscbt.com";

// Create axios instance with default configuration
const axiosInstance = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to handle auth tokens
axiosInstance.interceptors.request.use(
  (config) => {
    // 토큰을 스토어에서 가져오기
    const token = useLoginStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle standard API response format
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle unauthorized errors (e.g., token expired)
    if (error.response?.status === 401) {
      // 스토어에서 토큰 제거
      useLoginStore.getState().clearToken();

      // 현재 페이지가 로그인 페이지가 아닌 경우에만 리다이렉트
      if (window.location.pathname !== "/login") {
        // 커스텀 이벤트를 발생시켜 React 컴포넌트에서 처리할 수 있도록 함
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));

        // SPA 컨텍스트 밖에서는 직접 URL 변경이 필요할 수 있음
        setTimeout(() => {
          window.location.href = "/login";
        }, 100);
      }
    }

    // JSON 파싱 오류 또는 응답이 JSON 형식이 아닌 경우에만 처리
    if (
      !error.response ||
      !error.response.data ||
      typeof error.response.data !== "object"
    ) {
      const standardError: IApiResponse<null> = {
        success: false,
        message: "서버와의 통신 중 오류가 발생했습니다.",
        data: null,
        error: {
          code: error.response?.status
            ? `HTTP_${error.response.status}`
            : "NETWORK_ERROR",
          detail: error.message || error.toString(),
        },
      };
      return Promise.reject(standardError);
    }

    // 서버에서 정의된 형식의 오류 응답은 그대로 반환
    return Promise.reject(error.response.data);
  }
);

// 간결한 API 요청 함수들
export const apiGet = async <T>(
  url: string,
  params?: Record<string, any>
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.get(url, { params });
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiPost = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.post(url, data, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiPut = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.put(url, data, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiPatch = async <T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.patch(url, data, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export const apiDelete = async <T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<IApiResponse<T>> => {
  try {
    const response = await axiosInstance.delete(url, config);
    return response.data as IApiResponse<T>;
  } catch (error: any) {
    throw error; // 이미 인터셉터에서 처리된 오류를 그대로 전달
  }
};

export default axiosInstance;
